import { FileCheck2, Lock, PenLine } from "lucide-react";
import { PermissionTooltip, usePermission } from "../permissions/PermissionTooltip";
import { nextMinorVersion } from "../../types/issuedVersion";

export interface EditorVersionBannerProps {
  /** Last issued version, e.g. "1.1". Null when never issued. */
  currentVersion: string | null | undefined;
  /** Issue date of `currentVersion`. Optional; shown when present. */
  currentVersionIssuedAt?: Date | null;
  /** "1.2" while a revision is in progress; null when locked. */
  draftTargetVersion: string | null | undefined;
  /** True when issued and no draft has been started yet. */
  editLocked: boolean | undefined;

  onStartRevision: () => void;
  onApproveAndIssue: () => void;
  onDiscardDraft?: () => void;

  /** Optional last-saved timestamp shown in the draft state. */
  lastSavedAt?: Date | null;
  /** Disable the action buttons (e.g. while draft autosave is in flight). */
  busy?: boolean;
  /** Tooltip explaining why issuing is blocked (e.g. "3 sections still need review"). */
  issueBlockedReason?: string | null;
}

/**
 * Three-state banner that sits on top of any document editor:
 *
 *   - Never issued: prompt to issue v1.0
 *   - Locked:       show issued version + Start revision button
 *   - Draft:        show draft target + Approve & Issue / Discard buttons
 */
export function EditorVersionBanner(props: EditorVersionBannerProps) {
  const {
    currentVersion,
    currentVersionIssuedAt,
    draftTargetVersion,
    editLocked,
    onStartRevision,
    onApproveAndIssue,
    onDiscardDraft,
    lastSavedAt,
    busy,
    issueBlockedReason,
  } = props;

  const canIssue = usePermission("issue_documents");

  const isLocked = !!editLocked && !draftTargetVersion;
  const isDraft = !!draftTargetVersion;
  const isNeverIssued = !currentVersion && !isDraft && !isLocked;

  if (isNeverIssued) {
    return (
      <Banner tone="amber" icon={<FileCheck2 className="h-4 w-4" />}>
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-900">
            This document has not been issued yet.
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Issuing creates an immutable v1.0 snapshot for sponsor and auditor records.
          </div>
        </div>
        <PermissionTooltip privilege="issue_documents">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1.5"
            onClick={onApproveAndIssue}
            disabled={!canIssue || busy || !!issueBlockedReason}
            title={issueBlockedReason || undefined}
          >
            <FileCheck2 className="h-4 w-4" />
            Approve &amp; Issue v1.0
          </button>
        </PermissionTooltip>
      </Banner>
    );
  }

  if (isDraft) {
    return (
      <Banner tone="blue" icon={<PenLine className="h-4 w-4" />}>
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-900">
            Editing draft of v{draftTargetVersion}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {lastSavedAt
              ? `Last saved ${formatRelative(lastSavedAt)}`
              : currentVersion
              ? `Currently issued: v${currentVersion}`
              : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onDiscardDraft && (
            <button
              type="button"
              className="btn-secondary"
              onClick={onDiscardDraft}
              disabled={busy}
            >
              Discard draft
            </button>
          )}
          <PermissionTooltip privilege="issue_documents">
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1.5"
              onClick={onApproveAndIssue}
              disabled={!canIssue || busy || !!issueBlockedReason}
              title={issueBlockedReason || undefined}
            >
              <FileCheck2 className="h-4 w-4" />
              Approve &amp; Issue
            </button>
          </PermissionTooltip>
        </div>
      </Banner>
    );
  }

  // Locked
  const targetNext = nextMinorVersion(currentVersion);
  return (
    <Banner tone="teal" icon={<Lock className="h-4 w-4" />}>
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900">
          v{currentVersion} issued
          {currentVersionIssuedAt
            ? ` ${formatAbsolute(currentVersionIssuedAt)}`
            : ""}
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          To make changes, start a revision (will draft v{targetNext}).
        </div>
      </div>
      <PermissionTooltip privilege="issue_documents">
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-1.5"
          onClick={onStartRevision}
          disabled={!canIssue || busy}
        >
          <PenLine className="h-4 w-4" />
          Start revision
        </button>
      </PermissionTooltip>
    </Banner>
  );
}

interface BannerProps {
  tone: "amber" | "blue" | "teal";
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Banner({ tone, icon, children }: BannerProps) {
  const toneClasses: Record<BannerProps["tone"], string> = {
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    teal: "bg-teal-50 border-teal-200 text-teal-700",
  };
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 border rounded-lg ${toneClasses[tone]} mb-4`}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      {children}
    </div>
  );
}

function formatAbsolute(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return formatAbsolute(date);
}
